import { Popover, PopoverContent, PopoverTrigger } from '@ibsys/kit';
import { PencilIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ManualCommentUpdateRequest, useUpdateExpertReportRuleManualComment } from '@/entities/expert-report';
import { useTimeDisplayFormat } from '@/entities/settings';
import { IconButton } from '@/shared/ui';
import { isBlankRichText, joinCommentRemarks, toEditorComment } from '../lib/commentRemark';
import { formatCommentTimecodes, normalizeCommentTimecodes } from '../lib/commentTimecodes';
import { EvaluationRuleCommentField } from './EvaluationRuleCommentField';
import { EvaluationRuleCommentTabs, type RuleCommentTab } from './EvaluationRuleCommentTabs';

type Props = {
  conversationId: number;
  expertReportId: number;
  ruleId: number;
  /** Действующий комментарий правила: изменённый пользователем, иначе составленный системой */
  effectiveComment: string;
  /** Комментарий, изменённый пользователем; null — правка не сохранялась */
  manualComment: string | null;
  /** Действующие замечания к комментарию по порядку сохранения */
  remarks: readonly string[];
};

/** Содержимое окна правки: у каждой вкладки свой текст, пока окно открыто. */
type CommentDraft = {
  tab: RuleCommentTab;
  /** Замечание, набранное на вкладке `remark` */
  remark: string;
  /** Текст комментария на вкладке `text` */
  text: string;
  /** Текст вкладки `text` на момент открытия окна — пока он не тронут, сохранять нечего */
  openedText: string;
};

/**
 * Правка комментария правила: кнопка стоит у названия правила, окно открывается в поповере
 * на вкладке замечания и сохраняется по Shift+Enter или при уходе фокуса; крестик и Escape
 * закрывают окно без сохранения. Сохраняется вкладка, открытая в этот момент.
 *
 * Замечание уходит само по себе и текст комментария не меняет; пустое замечание — не правка.
 * Вкладка правки текста открывается комментарием вместе с замечаниями и сохраняется текстом целиком;
 * пустое поле означает сброс — комментарий возвращается к составленному системой, замечания снимаются.
 *
 * Таймкоды показываются и набираются в формате отображения отчётов эксперта,
 * а хранятся единой записью — приведение к ней проходит незаметно для пользователя.
 */
export const EvaluationRuleCommentEditor: FC<Props> = ({
  conversationId,
  expertReportId,
  ruleId,
  effectiveComment,
  manualComment,
  remarks,
}) => {
  const { t } = useTranslation(['dialogs']);
  const timeDisplayFormat = useTimeDisplayFormat('expertReport');
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<CommentDraft>({ tab: 'remark', remark: '', text: '', openedText: '' });
  const isCancelledRef = useRef(false);
  const { mutateAsync, isPending } = useUpdateExpertReportRuleManualComment();

  /** Сохранение по открытой вкладке; undefined — сохранять нечего */
  const resolveUpdate = useCallback((): ManualCommentUpdateRequest | undefined => {
    if (draft.tab === 'remark') {
      if (isBlankRichText(draft.remark)) return undefined;
      return { remark: normalizeCommentTimecodes(draft.remark, timeDisplayFormat) };
    }

    if (draft.text === draft.openedText) return undefined;
    const normalized = normalizeCommentTimecodes(draft.text, timeDisplayFormat);
    const nextManual = normalized.trim() === '' ? null : normalized;
    // Текст, совпавший с комментарием системы, — не правка, а сам комментарий системы
    const isSystemText =
      nextManual !== null && manualComment === null && nextManual === toEditorComment(effectiveComment);
    const next = isSystemText ? null : nextManual;
    // Сохранение того же текста меняет комментарий, только пока есть замечания: оно их снимает
    if (next === manualComment && remarks.length === 0) return undefined;
    return { manualComment: next };
  }, [draft, effectiveComment, manualComment, remarks, timeDisplayFormat]);

  const saveValue = useCallback(async () => {
    const update = resolveUpdate();
    if (update === undefined) return;
    try {
      await mutateAsync({ conversationId, expertReportId, ruleId, ...update });
    } catch {
      // Ошибку показал глобальный перехватчик запросов; при следующем открытии окно начнётся с сохранённого значения
    }
  }, [conversationId, expertReportId, ruleId, resolveUpdate, mutateAsync]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        // Каждое открытие начинается с чистой пометки: иначе отмена прошлой правки погасила бы
        // сохранение следующей
        isCancelledRef.current = false;
        const openedText = formatCommentTimecodes(joinCommentRemarks(effectiveComment, remarks), timeDisplayFormat);
        setDraft({ tab: 'remark', remark: '', text: openedText, openedText });
      } else if (isCancelledRef.current) {
        isCancelledRef.current = false;
      } else {
        void saveValue();
      }
      setIsOpen(open);
    },
    [saveValue, effectiveComment, remarks, timeDisplayFormat],
  );

  const handleCancel = useCallback(() => {
    isCancelledRef.current = true;
    // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — закрываем общим путём:
    // иначе набранный текст не сохранится
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleTabChange = useCallback((tab: RuleCommentTab) => setDraft((current) => ({ ...current, tab })), []);

  // Вкладка берётся из рендера, а не из состояния на момент правки: поле ушедшей вкладки, успей оно
  // что-то сообщить до размонтирования, не запишет свой текст в соседнюю
  const activeTab = draft.tab;
  const handleFieldChange = useCallback(
    (html: string) => setDraft((current) => ({ ...current, [activeTab]: html })),
    [activeTab],
  );

  /**
   * Escape закрывает окно отменой.
   *
   * Клавишу перехватывает сам Popover, и делает это раньше поля: без этой пометки закрытие пошло бы
   * обычным путём — с сохранением, то есть Escape сохранял бы правку вместо того, чтобы отменить её.
   */
  const handleEscapeKeyDown = useCallback(() => {
    isCancelledRef.current = true;
  }, []);

  /**
   * Popover при открытии фокусирует первый элемент содержимого — кнопку вкладки, и её подсказка
   * всплывает по фокусу поверх соседнего текста. Курсор в поле ставит само поле: этого и ждёт правка.
   */
  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        // Контролируемый Popover не зовёт onOpenChange при закрытии снаружи — сохраняем явно
        handleOpenChange(false);
      }
      // Escape здесь не обрабатываем: клавишу перехватывает сам Popover, он же закрывает окно —
      // отмену помечает `handleEscapeKeyDown`
    },
    [handleOpenChange],
  );

  // Подсказка называет ту запись таймкода, которую поле сейчас ждёт от пользователя
  const syntaxHintKey =
    timeDisplayFormat === 'SECOND'
      ? 'dialogs:expertReport.commentSyntaxHintSeconds'
      : 'dialogs:expertReport.commentSyntaxHint';

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <IconButton
        size="icon"
        className="size-5 align-middle"
        label={t('dialogs:expertReport.editComment')}
        icon={<PencilIcon className="size-3" />}
        trigger={PopoverTrigger}
      />
      <PopoverContent
        className="w-96 p-3"
        align="start"
        onOpenAutoFocus={handleOpenAutoFocus}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <div className="flex flex-1 flex-col gap-1.5">
              <EvaluationRuleCommentTabs activeTab={draft.tab} onTabChange={handleTabChange} disabled={isPending} />
              {/* Своё поле на вкладку: редактор, переписанный чужим текстом, потерял бы набранное и место курсора */}
              <EvaluationRuleCommentField
                key={draft.tab}
                value={draft[draft.tab]}
                onChange={handleFieldChange}
                onKeyDown={handleKeyDown}
                readOnly={isPending}
              />
            </div>
            <IconButton
              variant="ghost"
              size="icon"
              className="size-6"
              label={t('dialogs:expertReport.close')}
              icon={<XIcon className="size-3" />}
              onClick={handleCancel}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t(syntaxHintKey)}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
